// Base API configuration
const API_BASE_URL = '/api';

// Types for API responses and errors
export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

export class ApiException extends Error {
  public status: number;
  public errors?: Record<string, string[]>;

  constructor(message: string, status: number, errors?: Record<string, string[]>) {
    super(message);
    this.status = status;
    this.errors = errors;
    this.name = 'ApiException';
  }
}

// Helper function to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: any = {};
    try {
      errorData = await response.json();
    } catch {
      // If JSON parsing fails, use default error message
    }

    const message = errorData.message || `HTTP ${response.status} ${response.statusText}`;
    throw new ApiException(message, response.status, errorData.errors);
  }

  return response.json();
}

// Common fetch configuration
function getRequestConfig(method: string, data?: any): RequestInit {
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    credentials: 'include',
  };

  if (data && (method === 'POST' || method === 'PUT')) {
    config.body = JSON.stringify(data);
  }

  return config;
}

// Generic API request function
async function apiRequest<T>(endpoint: string, method: string, data?: any): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, getRequestConfig(method, data));
  return handleResponse<T>(response);
}

// HTTP method wrappers
export async function apiGet<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = params
    ? `${endpoint}?${new URLSearchParams(params).toString()}`
    : endpoint;
  return apiRequest<T>(url, 'GET');
}

export async function apiPost<T>(endpoint: string, data?: any): Promise<T> {
  return apiRequest<T>(endpoint, 'POST', data);
}

export async function apiPut<T>(endpoint: string, data?: any): Promise<T> {
  return apiRequest<T>(endpoint, 'PUT', data);
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, 'DELETE');
}
